import { useCallback, useEffect, useState } from "react";
import { uniq } from "lodash";

const useCollapseGroup = ({
    initialExpandedIds = [],
    idsDelayedTime = 400,
    sessionStorageKey = undefined,
}: {
    initialExpandedIds?: Array<string>,
    idsDelayedTime?: number,
    sessionStorageKey?: string,
}) => {
    const storedExpandedIdsStringified = sessionStorageKey ? sessionStorage.getItem(sessionStorageKey) : null;
    const storedExpandedIds = storedExpandedIdsStringified ? JSON.parse(storedExpandedIdsStringified) : null;
    const [expandedIds, _setExpandedIds] = useState<any>(
        sessionStorageKey ? (storedExpandedIds || initialExpandedIds) : initialExpandedIds
    );

    const [expandedIdsDelayed, setExpandedIdsDelayed] = useState<any>([]);

    const getIsIdExpanded = (id: string|number, prefix?: string) => expandedIds.includes((prefix ?? '') + id);

    const getIsIdExpandedDelayed = (id: string) => expandedIdsDelayed.includes(id);

    const setExpandedIds = useCallback((ids: Array<string> = [], prefix?: string) => {
        _setExpandedIds((prev: Array<string>) => {
            let newExpandedIds = [...prev];

            if (prefix) {
                newExpandedIds = newExpandedIds.filter((id) => id.indexOf(prefix) < 0);

                ids.forEach((id: string) => {
                    newExpandedIds.push(prefix + id);
                });
            } else {
                newExpandedIds = [...newExpandedIds, ...ids];
            }

            return uniq(newExpandedIds);
        });

        setTimeout(() => {
            setExpandedIdsDelayed((prev: Array<string>) => {
                let newExpandedIds = [...prev];

                if (prefix) {
                    newExpandedIds = newExpandedIds.filter((id) => id.indexOf(prefix) < 0);

                    ids.forEach((id: string) => {
                        newExpandedIds.push(prefix + id);
                    });
                } else {
                    newExpandedIds = [...newExpandedIds, ...ids];
                }

                return uniq(newExpandedIds);
            });
        }, idsDelayedTime);
    }, [_setExpandedIds, setExpandedIdsDelayed, idsDelayedTime]);

    const addExpandedId = useCallback((id: string|number, prefix?: string) => {
        const newId = (prefix ?? '') + id;

        _setExpandedIds((prev: Array<string>) => {
            const newExpandedIds = [...prev];
            if (!newExpandedIds.includes(newId)) {
                newExpandedIds.push(newId);

                setTimeout(() => {
                    setExpandedIdsDelayed(newExpandedIds);
                }, idsDelayedTime);
            }

            return newExpandedIds;
        });
    }, [idsDelayedTime]);

    const removeExpandedId = useCallback((id: string|number, prefix?: string) => {
        const newId = (prefix ?? '') + id;

        _setExpandedIds((prev: Array<string>) => {
            const newExpandedIds = [...prev];
            if (newExpandedIds.includes(newId)) {
                newExpandedIds.splice(newExpandedIds.indexOf(newId), 1);

                setTimeout(() => {
                    setExpandedIdsDelayed(newExpandedIds);
                }, idsDelayedTime);
            }

            return newExpandedIds;
        });
    }, [idsDelayedTime]);

    const toggleExpandedId = (
        id: string|number,
        prefix?: string,
        { scrollRef }: { scrollRef?: HTMLDivElement } = {},
    ) => {
        let added = false;
        let newId = (prefix ?? '') + id;

        _setExpandedIds((prev: string[]) => {
            const newExpandedRegistrantIds = [...prev];

            if (newExpandedRegistrantIds.includes(newId)) {
                newExpandedRegistrantIds.splice(newExpandedRegistrantIds.indexOf(newId), 1);
            } else {
                newExpandedRegistrantIds.push(newId);
                added = true;
            }

            if (newExpandedRegistrantIds.length > prev.length) {
                setTimeout(() => {
                    setExpandedIdsDelayed(newExpandedRegistrantIds)
                }, idsDelayedTime);
            } else setExpandedIdsDelayed(newExpandedRegistrantIds);

            return newExpandedRegistrantIds;
        });

        if (scrollRef && added) {
            scrollRef.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center",
            });
        }

        return added;
    }

    useEffect(() => {
        if (!sessionStorageKey) return;

        sessionStorage.setItem(sessionStorageKey, JSON.stringify(expandedIds));
    }, [sessionStorageKey, expandedIds]);

    return {
        expandedIds,
        expandedIdsDelayed,
        setExpandedIds,
        directStateSetExpandedIds: _setExpandedIds,
        getIsIdExpanded,
        getIsIdExpandedDelayed,
        addExpandedId,
        removeExpandedId,
        toggleExpandedId,
    };
};

export default useCollapseGroup;
